import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

interface ArticleLayoutProps {
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalUrl: string;
  alternateUrlFr: string;
  alternateUrlEn: string;
  category: string;
  categoryColor: 'blue' | 'emerald' | 'purple';
  title: string;
  subtitle: string;
  date: string;
  dateISO: string;
  readTime: string;
  content: string;
  structuredData?: object | object[];
}

export const ArticleLayout: React.FC<ArticleLayoutProps> = ({
  seoTitle, seoDescription, seoKeywords, canonicalUrl,
  alternateUrlFr, alternateUrlEn, category, categoryColor,
  title, subtitle, date, dateISO, readTime, content, structuredData
}) => {
  const { language } = useLanguage();
  

  const colorMap = {
    blue: 'bg-blue-100 text-blue-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    purple: 'bg-purple-100 text-purple-800'
  };

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": seoDescription,
    "datePublished": dateISO,
    "dateModified": dateISO,
    "author": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com",
      "description": "Cabinet de formation et conseil en intelligence artificielle juridique pour avocats et juristes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://gaius-tech.com/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
        alternateUrlFr={alternateUrlFr}
        alternateUrlEn={alternateUrlEn}
        structuredData={structuredData || defaultStructuredData}
        pageType="article" />


      <article className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={language === 'fr' ? '/blog' : '/blog'}
            className="inline-flex items-center text-primary hover:underline mb-8 text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
          </Link>

          <header className="mb-12 bg-slate-100">
            <Badge variant="secondary" className={`${colorMap[categoryColor]} mb-4`}>
              {category}
            </Badge>
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4 leading-tight">
              {title}
            </h1>
            <p className="text-lg text-muted-foreground mb-6">{subtitle}</p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{date}</span>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{readTime}</span>
              </span>
            </div>
          </header>

          <div className="article-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) =>
                <h2 className="text-2xl font-bold text-foreground mt-12 mb-6">{children}</h2>,

                h3: ({ children }) =>
                <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">{children}</h3>,

                h4: ({ children }) =>
                <h4 className="text-lg font-semibold text-foreground mt-6 mb-3">{children}</h4>,

                p: ({ children }) =>
                <p className="text-muted-foreground leading-relaxed mb-6">{children}</p>,

                a: ({ href, children }) => {
                  if (href?.startsWith('/')) {
                    return <Link to={href} className="text-primary hover:underline font-medium">{children}</Link>;
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                      {children}
                    </a>);

                },
                ul: ({ children }) =>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-muted-foreground">{children}</ul>,

                ol: ({ children }) =>
                <ol className="list-decimal pl-6 space-y-2 mb-6 text-muted-foreground">{children}</ol>,

                li: ({ children }) =>
                <li className="leading-relaxed">{children}</li>,

                strong: ({ children }) =>
                <strong className="font-semibold text-foreground">{children}</strong>,

                em: ({ children }) =>
                <em className="italic">{children}</em>,

                blockquote: ({ children }) =>
                <blockquote className="border-l-4 border-primary/30 pl-6 italic text-muted-foreground my-8 py-2">{children}</blockquote>,

                table: ({ children }) =>
                <div className="overflow-x-auto my-8">
                    <table className="w-full border-collapse border border-border text-sm">{children}</table>
                  </div>,

                thead: ({ children }) =>
                <thead className="bg-secondary">{children}</thead>,

                th: ({ children }) =>
                <th className="border border-border px-4 py-3 text-left font-semibold text-foreground">{children}</th>,

                td: ({ children }) =>
                <td className="border border-border px-4 py-3 text-muted-foreground">{children}</td>,

                hr: () => <hr className="my-10 border-border" />,
                code: ({ className, children }) => {
                  const isBlock = className?.includes('language-');
                  if (isBlock) {
                    return (
                      <code className="text-sm font-mono text-foreground">{children}</code>);

                  }
                  return <code className="bg-secondary px-1.5 py-0.5 rounded text-sm font-mono text-foreground">{children}</code>;
                },
                pre: ({ children }) =>
                <pre className="bg-secondary rounded-lg p-4 overflow-x-auto my-6">{children}</pre>

              }}>

              {content}
            </ReactMarkdown>
          </div>

          <footer className="mt-16 pt-8 border-t border-border">
            <p className="text-muted-foreground italic">
              <strong className="text-foreground">À propos de Gaius :</strong>{' '}
              Notre équipe de formateurs en IA juridique accompagne les avocats et juristes dans leur transformation numérique. Retrouvez nos analyses et formations sur{' '}
              <a href="https://www.gaius-tech.com" className="text-primary hover:underline">www.gaius-tech.com</a>{' '}
              et sur notre page{' '}
              <a href="https://www.linkedin.com/company/gaius-tech/about/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">LinkedIn</a>.
            </p>
          </footer>
        </div>
      </article>
    </div>);

};